import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const TrustIndicators = () => {
  const certifications = [
    {
      icon: "Shield",
      title: "Certifié ISO 9001",
      description: "Qualité garantie selon standards internationaux"
    },
    {
      icon: "Award",
      title: "Licence RDC",
      description: "Enregistrement officiel République Démocratique du Congo"
    },
    {
      icon: "CheckCircle",
      title: "Conformité Légale",
      description: "Respect total des réglementations congolaises"
    },
    {
      icon: "Lock",
      title: "Données Sécurisées",
      description: "Protection maximale de vos informations"
    }
  ];

  const stats = [
    { value: "500+", label: "Clients Actifs", icon: "Users" },
    { value: "12+", label: "Services Premium", icon: "Briefcase" },
    { value: "15+", label: "Années d'Expérience", icon: "Calendar" },
    { value: "98%", label: "Satisfaction Client", icon: "ThumbsUp" }
  ];

  const partners = [
    {
      name: "Ministère du Commerce RDC",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_12112e83d-1767533473851.png",
      logoAlt: "Official seal of Democratic Republic of Congo Ministry of Commerce featuring national emblem in blue and gold colors"
    },
    {
      name: "Chambre de Commerce Kinshasa",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1aaa3dec6-1768070439308.png",
      logoAlt: "Kinshasa Chamber of Commerce logo with handshake symbol and DRC flag colors representing business partnership"
    },
    {
      name: "Association Professionnelle IT",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1fabf64e1-1769301924698.png",
      logoAlt: "Professional IT Association logo featuring circuit board pattern and technology symbols in modern design"
    },
    {
      name: "Fédération Immobilière RDC",
      logo: "https://img.rocket.new/generatedImages/rocket_gen_img_18d12270c-1769301924742.png",
      logoAlt: "DRC Real Estate Federation logo with building silhouette and professional certification badge in corporate colors"
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-white dark:bg-slate-900 relative overflow-hidden border-t border-slate-200 dark:border-white/5">
      
      {/* Background Decor */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-slate-800 via-slate-900 to-black opacity-0 dark:opacity-80" />
      <div className="absolute inset-0 opacity-0 dark:opacity-10 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] mix-blend-overlay"></div>
      
      {/* Animated Blobs */}
      <motion.div 
        animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 10, repeat: Infinity }}
        className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-600/5 dark:bg-blue-600/10 rounded-full blur-[150px]"
      />
      <motion.div
        animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 8, repeat: Infinity, delay: 2 }}
        className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-amber-500/5 dark:bg-amber-500/10 rounded-full blur-[150px]" 
      />

      {/* CONTAINER ÉLARGI : max-w-[1920px] */}
      <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 relative z-10">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20">

          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-full mb-6">
            <Icon name="ShieldCheck" size={16} className="text-blue-500 dark:text-blue-400" />
            <span className="text-sm font-medium text-blue-600 dark:text-blue-300 uppercase tracking-wider">Confiance & Transparence</span>
          </div>

          <h2 className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white mb-6">
            Votre Partenaire <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-amber-400">
              Certifié & Fiable
            </span>
          </h2>
          
          <p className="text-xl text-slate-600 dark:text-slate-400 max-w-3xl mx-auto leading-relaxed">
            Certifications officielles et reconnaissance professionnelle garantissant la qualité supérieure de nos services en RDC.
          </p>
        </motion.div>

        {/* Certifications Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-24">

          {certifications.map((cert, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 * index }}
              whileHover={{ y: -5 }}
              className="bg-slate-50 dark:bg-slate-800/40 backdrop-blur-md border border-slate-200 dark:border-white/10 rounded-3xl p-10 text-center hover:border-blue-500/30 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-all duration-300 shadow-xl group">

              <div className="w-20 h-20 mx-auto mb-8 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center shadow-lg shadow-blue-900/30 group-hover:scale-110 transition-transform duration-300">
                <Icon name={cert.icon} size={36} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">
                {cert.title}
              </h3>
              <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                {cert.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Stats Section Wide */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-r from-blue-50 dark:from-blue-900/20 to-slate-50 dark:to-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-white/5 rounded-[2.5rem] p-12 md:p-20 mb-24 relative overflow-hidden shadow-2xl">

          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] mix-blend-overlay" />

          <div className="relative z-10 grid grid-cols-2 lg:grid-cols-4 gap-12 md:gap-20">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index }}
                className="text-center group">

                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10 flex items-center justify-center group-hover:bg-amber-500/20 group-hover:border-amber-500/50 transition-all duration-300">
                  <Icon name={stat.icon} size={32} className="text-amber-400" />
                </div>
                <div className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white mb-3 tracking-tight">
                  {stat.value}
                </div>
                <div className="text-base md:text-lg text-slate-600 dark:text-slate-400 uppercase tracking-widest font-semibold">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Partners Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center">

          <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-12 flex items-center justify-center gap-4">
            <span className="h-px w-12 bg-slate-300 dark:bg-slate-700"></span>
            Nos Partenaires & Affiliations
            <span className="h-px w-12 bg-slate-300 dark:bg-slate-700"></span>
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {partners.map((partner, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index }}
                whileHover={{ scale: 1.02 }}
                className="bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-xl hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-300 group flex flex-col items-center justify-center h-full border border-slate-200 dark:border-slate-700">

                <div className="w-full h-24 flex items-center justify-center mb-4">
                  <img
                    src={partner.logo}
                    alt={partner.logoAlt}
                    className="max-w-full max-h-full object-contain filter grayscale group-hover:grayscale-0 opacity-60 group-hover:opacity-100 transition-all duration-300" 
                  />
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider group-hover:text-blue-700 dark:group-hover:text-blue-400 transition-colors">
                  {partner.name}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Trust Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="mt-20 inline-flex items-center gap-4 px-8 py-4 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full shadow-lg hover:border-green-500/50 transition-colors">

            <div className="bg-green-500/10 p-2 rounded-full">
                <Icon name="BadgeCheck" size={24} className="text-green-400" />
            </div>
            <span className="text-base font-semibold text-slate-700 dark:text-slate-200">
              Entreprise Vérifiée & Certifiée RDC
            </span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default TrustIndicators;